import { Link } from 'react-router-dom';
import { ArrowRight, Target, Eye, CheckCircle, Award, Users, Globe, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import SEO from '@/components/SEO';

const milestones = [];

const values = [
  {
    icon: Target,
    title: 'Results-Driven',
    description: 'We measure success by the tangible outcomes we deliver for your enterprise.'
  },
  {
    icon: Shield,
    title: 'Trust & Integrity',
    description: 'We build lasting partnerships through transparency and ethical practices.'
  },
  {
    icon: Users,
    title: 'Client-Centric',
    description: 'Your success is our success. We align our goals with your business objectives.'
  },
  {
    icon: Award,
    title: 'Excellence',
    description: 'We pursue the highest standards in every Oracle solution we deliver.'
  }
];

export default function AboutPage() {
  return (
    <div className="pt-20" data-testid="about-page">
      <SEO 
        title="About Us"
        description="Learn about Trid Enterprise Solutions - your trusted Oracle transformation partner with 10+ years of experience delivering enterprise excellence globally."
        keywords="About Trid Enterprise, Oracle Partner, Enterprise Solutions, Company History, Oracle Consulting Firm"
      />
      {/* Hero Section */}
      <section className="section-dark py-12 md:py-20 lg:py-32 relative overflow-hidden">
        <div className="absolute top-1/2 right-0 w-48 md:w-96 h-48 md:h-96 bg-oracle-red/10 rounded-full blur-3xl" />
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="text-center lg:text-left">
              <span className="eyebrow mb-3 md:mb-4 inline-block text-xs">About Trid Enterprise</span>
              <h1 className="heading-xl text-white mb-4 md:mb-6 font-heading">
                Your Trusted
                <span className="block text-oracle-red">Oracle Partner</span>
              </h1>
              <p className="text-slate-300 font-body text-base md:text-lg lg:text-xl leading-relaxed">
                For over 15 years, Trid Enterprise Solutions has been at the forefront of Oracle transformation, helping enterprises unlock their full potential through expert implementations, migrations, and custom solutions.
              </p>
            </div>
            <div className="relative mt-6 lg:mt-0">
              <img 
                src="https://images.unsplash.com/photo-1758691737278-3af15b37af48?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjB0ZWFtJTIwbWVldGluZyUyMGdsYXNzJTIwb2ZmaWNlfGVufDB8fHx8MTc3MDY1MzE2Mnww&ixlib=rb-4.1.0&q=85"
                alt="Trid Enterprise Team"
                className="rounded-sm shadow-2xl"
              />
              <div className="absolute -bottom-4 -left-4 md:-bottom-6 md:-left-6 bg-oracle-red p-4 md:p-6 rounded-sm shadow-xl">
                <div className="font-heading text-2xl md:text-4xl font-bold text-white">10+</div>
                <div className="font-body text-xs md:text-sm text-white/80">Years of Excellence</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-light py-12 md:py-20 lg:py-32" data-testid="mission-vision">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-6 md:gap-8 lg:gap-12">
            <Card className="card-enterprise p-5 md:p-6 lg:p-8">
              <CardContent className="p-0">
                <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 mb-4 md:mb-6">
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-oracle-red/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Target className="w-6 h-6 md:w-7 md:h-7 text-oracle-red" />
                  </div>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-slate-900">Our Mission</h2>
                </div>
                <p className="font-body text-slate-700 text-sm md:text-base leading-relaxed">
                  To empower enterprises with transformative Oracle solutions that drive operational excellence, accelerate growth, and deliver measurable business outcomes. We are committed to being the trusted partner that turns complex challenges into strategic advantages.
                </p>
              </CardContent>
            </Card>
            
            <Card className="card-enterprise p-5 md:p-6 lg:p-8">
              <CardContent className="p-0">
                <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 mb-4 md:mb-6">
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-oracle-red/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Eye className="w-6 h-6 md:w-7 md:h-7 text-oracle-red" />
                  </div>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-slate-900">Our Vision</h2>
                </div>
                <p className="font-body text-slate-700 text-sm md:text-base leading-relaxed">
                  To be the global leader in Oracle enterprise transformation, recognized for our unwavering commitment to client success, technical excellence, and innovative solutions that shape the future of enterprise technology.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="section-dark py-12 md:py-20 lg:py-32" data-testid="our-values">
        <div className="container-custom">
          <div className="text-center mb-10 md:mb-16">
            <span className="eyebrow mb-3 md:mb-4 inline-block text-xs">What Drives Us</span>
            <h2 className="heading-lg text-white font-heading">Our Core Values</h2>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 lg:gap-8">
            {values.map((value, index) => (
              <Card 
                key={value.title}
                className="card-dark text-center"
                data-testid={`value-card-${index}`}
              >
                <CardContent className="p-4 md:p-6 lg:p-8">
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-oracle-red/20 rounded-sm flex items-center justify-center mx-auto mb-4 md:mb-6">
                    <value.icon className="w-6 h-6 md:w-8 md:h-8 text-oracle-red" />
                  </div>
                  <h3 className="font-heading text-base md:text-lg lg:text-xl font-bold text-white mb-2 md:mb-3">
                    {value.title}
                  </h3>
                  <p className="font-body text-slate-400 text-xs md:text-sm leading-relaxed">
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Global Presence */}
      <section className="section-light py-12 md:py-20 lg:py-32">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="text-center lg:text-left">
              <span className="eyebrow mb-3 md:mb-4 inline-block text-xs">Global Reach</span>
              <h2 className="heading-lg text-slate-900 mb-4 md:mb-6 font-heading">
                Serving Enterprises Worldwide
              </h2>
              <p className="font-body text-slate-700 text-sm md:text-base leading-relaxed mb-6 md:mb-8">
                With offices across North America, Europe, and Asia-Pacific, we deliver Oracle excellence to enterprises around the globe. Our distributed team ensures round-the-clock support and local expertise wherever you operate.
              </p>
              
              <div className="grid grid-cols-3 gap-4 md:gap-6">
                <div className="text-center">
                  <Globe className="w-6 h-6 md:w-8 md:h-8 text-oracle-red mx-auto mb-1 md:mb-2" />
                  <div className="font-heading text-xl md:text-2xl font-bold text-slate-900">3</div>
                  <div className="font-body text-[10px] md:text-xs text-slate-600">Regions</div>
                </div>
                <div className="text-center">
                  <Users className="w-6 h-6 md:w-8 md:h-8 text-oracle-red mx-auto mb-1 md:mb-2" />
                  <div className="font-heading text-xl md:text-2xl font-bold text-slate-900">50+</div>
                  <div className="font-body text-[10px] md:text-xs text-slate-600">Consultants</div>
                </div>
                <div className="text-center">
                  <Award className="w-6 h-6 md:w-8 md:h-8 text-oracle-red mx-auto mb-1 md:mb-2" />
                  <div className="font-heading text-xl md:text-2xl font-bold text-slate-900">100+</div>
                  <div className="font-body text-[10px] md:text-xs text-slate-600">Projects</div>
                </div>
              </div>
            </div>
            <div className="mt-6 lg:mt-0">
              <img 
                src="https://images.unsplash.com/photo-1758518730384-be3d205838e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGhhbmRzaGFrZSUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3NzA2NTMxNzJ8MA&ixlib=rb-4.1.0&q=85"
                alt="Global Partnership"
                className="rounded-sm shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-dark py-12 md:py-20 lg:py-32" data-testid="about-cta">
        <div className="container-custom text-center">
          <span className="eyebrow mb-3 md:mb-4 inline-block text-xs">Partner With Us</span>
          <h2 className="heading-lg text-white mb-4 md:mb-6 font-heading max-w-2xl mx-auto">
            Ready to Start Your Transformation Journey?
          </h2>
          <p className="text-slate-300 font-body text-sm md:text-base lg:text-lg mb-6 md:mb-8 max-w-xl mx-auto">
            Connect with our Oracle experts and discover how we can help achieve your enterprise goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
            <Link to="/contact" className="w-full sm:w-auto">
              <Button 
                size="lg" 
                className="w-full sm:w-auto bg-oracle-red hover:bg-oracle-red-dark text-white font-body px-6 md:px-8 py-5 md:py-6 text-sm md:text-base"
                data-testid="about-consultation-btn"
              >
                Schedule Consultation
                <ArrowRight className="ml-2 w-4 h-4 md:w-5 md:h-5" />
              </Button>
            </Link>
            <Link to="/case-studies" className="w-full sm:w-auto">
              <Button 
                size="lg" 
                variant="outline" 
                className="w-full sm:w-auto border-white text-white hover:bg-white hover:text-slate-900 font-body px-6 md:px-8 py-5 md:py-6 text-sm md:text-base"
              >
                View Our Work
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
